/**
 * Shared field ↔ screen coordinate maths for the AFL board.
 *
 * One source of truth so the ball overlay, the on-field player cards, and
 * the click-to-move disposal targets all line up. Coordinates are in the
 * 0..100 percent space of the Field SVG, drawn from the VIEWER's
 * perspective (their goal on the right). `attackingRight` is true when the
 * possessor attacks the viewer's (right) goal.
 */

export type FieldLine = 'd50' | 'hb' | 'centre' | 'hf' | 'f50';
export type FieldLane = 'flank_a' | 'corridor' | 'flank_b';

// Line position along the attacking axis (defensive end → goal).
const LINE_OFFSET: Record<string, number> = { d50: -2, hb: -1, centre: 0, hf: 1, f50: 2 };

/** Defensive end → goal. Mirrors the engine's BALL_LINE_ORDER (forward = later). */
export const BALL_LINE_ORDER: readonly FieldLine[] = ['d50', 'hb', 'centre', 'hf', 'f50'];

/** The cell one line forward (toward goal); clamps at f50 — matches `handleRun`. */
export function forwardLine(line: FieldLine): FieldLine {
  const i = BALL_LINE_ORDER.indexOf(line);
  return BALL_LINE_ORDER[Math.min(i + 1, BALL_LINE_ORDER.length - 1)]!;
}

/**
 * Map an engine zone (line + lane, in the possessor's attacking frame) to a
 * field coordinate from the viewer's perspective (their goal on the right).
 */
export function zoneToXY(line: string, lane: string, attackingRight: boolean): { x: number; y: number } {
  const off = LINE_OFFSET[line] ?? 0;
  const x = 50 + (attackingRight ? off : -off) * 18;
  const y = lane === 'flank_a' ? 30 : lane === 'flank_b' ? 70 : 50;
  return { x, y };
}

/**
 * Inverse of zoneToXY at the cell level: map a printed field cell (its grid
 * column 1/4/8/12/15 and row) back to the engine zone (line + lane) in the
 * POSSESSOR's frame — i.e. the same frame `onField` cards are stored in, so a
 * clicked cell can be matched against the players standing there. `attackingRight`
 * is the board's single frame flag (true when the frame seat attacks right).
 */
const COL_TO_OFFSET: Record<number, number> = { 1: -2, 4: -1, 8: 0, 12: 1, 15: 2 };
const OFFSET_TO_LINE: Record<number, FieldLine> = {
  '-2': 'd50', '-1': 'hb', 0: 'centre', 1: 'hf', 2: 'f50',
};
export function cellToZone(
  col: number,
  row: 'top' | 'centre' | 'bottom',
  attackingRight: boolean,
): { line: FieldLine; lane: FieldLane } {
  const base = COL_TO_OFFSET[col] ?? 0;
  const off = attackingRight ? base : -base;
  const line = OFFSET_TO_LINE[off] ?? 'centre';
  const lane: FieldLane = row === 'top' ? 'flank_a' : row === 'bottom' ? 'flank_b' : 'corridor';
  return { line, lane };
}

/**
 * Where the possessor's attacking goal sits on the drawn field. Kept a few
 * percent inside the goal line (posts are at 98/2) so the shot marker and
 * its label render fully without clipping at the field edge.
 */
export function goalXY(attackingRight: boolean): { x: number; y: number } {
  return { x: attackingRight ? 92 : 8, y: 50 };
}

// ---------------------------------------------------------------------------
// Zone names — the human label for the cell the ball is in.
// ---------------------------------------------------------------------------

// Names in the VIEWER's frame (their forward line = f50 on the right).
const ZONE_BASE: Record<string, string> = {
  'f50:corridor': 'Full Forward', 'f50:flank_a': 'Forward Pocket', 'f50:flank_b': 'Forward Pocket',
  'hf:corridor': 'Centre Half-Forward', 'hf:flank_a': 'Half-Forward Flank', 'hf:flank_b': 'Half-Forward Flank',
  'centre:corridor': 'Centre', 'centre:flank_a': 'Wing', 'centre:flank_b': 'Wing',
  'hb:corridor': 'Centre Half-Back', 'hb:flank_a': 'Half-Back Flank', 'hb:flank_b': 'Half-Back Flank',
  'd50:corridor': 'Full Back', 'd50:flank_a': 'Back Pocket', 'd50:flank_b': 'Back Pocket',
};

// The same physical cell is the possessor's forward line and the viewer's
// back line, so flip the line (not the lane — top/bottom don't move) when
// the possessor is attacking the LEFT goal.
const MIRROR_LINE: Record<FieldLine, FieldLine> = {
  d50: 'f50', hb: 'hf', centre: 'centre', hf: 'hb', f50: 'd50',
};

/**
 * Human name for the cell the ball sits in, in the VIEWER's frame so it
 * matches the printed field labels (e.g. "Forward Pocket (top)"). `line`/
 * `lane` are the engine's possessor-frame zone; `attackingRight` is true
 * when the possessor attacks the viewer's (right) goal.
 */
export function viewerZoneName(line: FieldLine, lane: FieldLane, attackingRight: boolean): string {
  const vLine = attackingRight ? line : MIRROR_LINE[line];
  const base = ZONE_BASE[`${vLine}:${lane}`] ?? '';
  const side = lane === 'flank_a' ? ' (top)' : lane === 'flank_b' ? ' (bottom)' : '';
  return base + side;
}
